import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { ShieldAlert, Clock, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '—';
  const hasOffset = iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso);
  const s = hasOffset ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Mexico_City',
  });
}

function motivoSuspension(user) {
  const today = new Date();
  const cedulaExpired = user?.cedula_vencimiento && new Date(user.cedula_vencimiento) < today;
  const rcExpired = user?.rc_vencimiento && new Date(user.rc_vencimiento) < today;
  if (cedulaExpired && rcExpired) return 'tu cédula profesional y tu póliza de responsabilidad civil (RC) vencieron';
  if (cedulaExpired) return 'tu cédula profesional venció';
  if (rcExpired) return 'tu póliza de responsabilidad civil (RC) venció';
  return 'tu cédula profesional o tu póliza de responsabilidad civil (RC) vencieron';
}

export const AccountSuspendedScreen = () => {
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState({
    cedula_inicio: '',
    cedula_vencimiento: '',
    rc_inicio: '',
    rc_vencimiento: '',
  });
  const [loading, setLoading] = useState(false);

  const canSubmit = form.cedula_vencimiento && form.rc_vencimiento;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const payload = {
        cedula_vencimiento: form.cedula_vencimiento,
        rc_vencimiento: form.rc_vencimiento,
        cedula_inicio: form.cedula_inicio || null,
        rc_inicio: form.rc_inicio || null,
      };
      const { data } = await axios.put(`${API}/users/me/request-reactivation`, payload);
      setUser(data);
      toast.success('Enviado. Tu administrador revisará y aprobará la reactivación.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar. Verifica las fechas.');
    } finally {
      setLoading(false);
    }
  };

  if (user?.pending_review) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full">
          <CardHeader className="items-center text-center">
            <Clock className="h-10 w-10 text-amber-500 mb-2" />
            <CardTitle>Pendiente de aprobación</CardTitle>
            <CardDescription className="space-y-3 text-center">
              <span className="block">
                Actualizaste tu información el {fmtDate(user.review_submitted_at)}. Un administrador
                debe revisarla y aprobarla antes de que puedas volver a usar el CRM.
              </span>
              <span className="block">
                Si cometiste un error en la fecha, contacta a tu administrador para que la corrija —
                por ahora no puedes reenviarla tú mismo.
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button variant="outline" onClick={() => logout()}>
              <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background px-4 py-8 overflow-y-auto">
      <Card className="max-w-md w-full">
        <CardHeader className="items-center text-center">
          <ShieldAlert className="h-10 w-10 text-red-500 mb-2" />
          <CardTitle>Cuenta suspendida</CardTitle>
          <CardDescription>
            Tu cuenta fue suspendida automáticamente porque {motivoSuspension(user)}. Actualiza tu
            fecha de vigencia abajo — quedará pendiente de aprobación de tu administrador, no se
            reactiva al instante.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="border rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">Cédula profesional</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Fecha de inicio</Label>
                  <Input
                    type="date"
                    value={form.cedula_inicio}
                    onChange={e => setForm(f => ({ ...f, cedula_inicio: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Vencimiento <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={form.cedula_vencimiento}
                    onChange={e => setForm(f => ({ ...f, cedula_vencimiento: e.target.value }))}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">Póliza RC</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Fecha de inicio</Label>
                  <Input
                    type="date"
                    value={form.rc_inicio}
                    onChange={e => setForm(f => ({ ...f, rc_inicio: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Vencimiento <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={form.rc_vencimiento}
                    onChange={e => setForm(f => ({ ...f, rc_vencimiento: e.target.value }))}
                    required
                  />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full bg-[#1a365d] hover:bg-[#1a365d]/90" disabled={loading || !canSubmit}>
              {loading ? 'Enviando...' : 'Enviar para revisión'}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => logout()}>
              <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
            </Button>
          </CardContent>
        </form>
      </Card>
    </div>
  );
};
